import { createRoute, useLocation } from '@tanstack/react-router'
import { projectRoute } from '../../route'
import { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { orpc, toApiUrl } from '@/lib/orpc-client'
import { useSetPageActions, useSetPageSubtitle } from '@/lib/page-title-context'
import { useAuthState } from '@/lib/auth-state'
import { DocumentActions } from '@/components/documents/document-actions'
import { useDocumentClient } from '@/lib/documents/use-document-client'
import { useCommentLayoutMode, useDocumentComments } from '@/lib/documents/use-document-comments'
import { buildPrintHtml, downloadBlob, inlineImages, printHtml } from '@/lib/documents/export-html'
import { CommentHighlightStyle } from '@/components/documents/comments/comment-highlight-style'
import { MeetingNotesBanner } from '@/components/documents/meeting-notes-banner'
import type { BlockEditorHandle, EditorHistoryState } from '@/components/documents/block-editor'
import {
  COMMENT_THREAD_ID_RE,
  fileExtensionOf,
  isImageDocumentExtension,
  type CommentThread,
} from '@hub/contracts'

const BlockEditor = lazy(() =>
  import('@/components/documents/block-editor').then((mod) => ({ default: mod.BlockEditor }))
)
const CommentMargin = lazy(() =>
  import('@/components/documents/comments/comment-margin').then((mod) => ({
    default: mod.CommentMargin,
  }))
)
const CommentFloating = lazy(() =>
  import('@/components/documents/comments/comment-floating').then((mod) => ({
    default: mod.CommentFloating,
  }))
)
const CommentDrawer = lazy(() =>
  import('@/components/documents/comments/comment-drawer').then((mod) => ({
    default: mod.CommentDrawer,
  }))
)
const CommentListSheet = lazy(() =>
  import('@/components/documents/comments/comment-list').then((mod) => ({
    default: mod.CommentListSheet,
  }))
)
const HubBubble = lazy(() =>
  import('@/components/chat/hub-bubble').then((mod) => ({ default: mod.HubBubble }))
)
const PdfViewer = lazy(() =>
  import('@/components/pdf-viewer').then((mod) => ({ default: mod.PdfViewer }))
)
const SpreadsheetViewer = lazy(() =>
  import('@/components/spreadsheet-viewer').then((mod) => ({ default: mod.SpreadsheetViewer }))
)
const ImageViewer = lazy(() =>
  import('@/components/image-viewer').then((mod) => ({ default: mod.ImageViewer }))
)

export const writeDetailRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/write/$docId',
  component: DocumentEditorPage,
})

function Spinner({ label }: { label: string }) {
  return (
    <div className="flex flex-1 items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
        <p className="text-muted-foreground">{label}</p>
      </div>
    </div>
  )
}

function DocumentEditorPage() {
  const { projectId, docId } = writeDetailRoute.useParams()
  const { t } = useTranslation('documents')
  const { t: te } = useTranslation('editor')
  const { me } = useAuthState()
  const [title, setTitle] = useState<string | undefined>()
  const [sourceFilePath, setSourceFilePath] = useState<string | null>(null)
  const [pdfPath, setPdfPath] = useState<string | null>(null)
  const [spreadsheetPath, setSpreadsheetPath] = useState<string | null>(null)
  const [imagePath, setImagePath] = useState<string | null>(null)
  const [description, setDescription] = useState<string | null>(null)
  const [originalFileName, setOriginalFileName] = useState<string | null>(null)
  const [conversionStatus, setConversionStatus] = useState<string | null>(null)
  // documents.get answered: until then we do not know whether there are blocks to open.
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(false)
  const [selectedBlockIds, setSelectedBlockIds] = useState<string[]>([])
  const [exporting, setExporting] = useState<'pdf' | 'docx' | null>(null)
  const [pdfAvailable, setPdfAvailable] = useState(false)
  const [history, setHistory] = useState<EditorHistoryState>({ canUndo: false, canRedo: false })
  const editorRef = useRef<BlockEditorHandle | null>(null)
  // The handle as state too: the margin subscribes to layout ticks once the editor exists.
  const [editorHandle, setEditorHandle] = useState<BlockEditorHandle | null>(null)
  const setEditorRef = useCallback((handle: BlockEditorHandle | null) => {
    editorRef.current = handle
    setEditorHandle(handle)
  }, [])
  const { mode: commentMode, rowRef, rowEl } = useCommentLayoutMode()
  // List (sheet / drawer) for the narrow and phone layouts; optional block filter.
  const [commentList, setCommentList] = useState<{ open: boolean; blockId: string | null }>({
    open: false,
    blockId: null,
  })

  const isUploadedDocument = !!sourceFilePath
  const isConverting = conversionStatus === 'converting' || conversionStatus === 'pending'
  // Image documents (board card 2u86) never have blocks: no editor, no bubble.
  const isImageDocument =
    !!sourceFilePath && isImageDocumentExtension(fileExtensionOf(sourceFilePath))
  // The block store is only opened for editable documents (not PDF/spreadsheet/image views).
  // Waiting for documents.get keeps a picture (or PDF) from ever creating a
  // DocumentStore — the socket used to connect before the row said what it was.
  const wantsEditor =
    loaded &&
    !error &&
    !isImageDocument &&
    !(isUploadedDocument && (pdfPath || spreadsheetPath || isConverting))
  const doc = useDocumentClient(projectId, docId, wantsEditor)
  const comments = useDocumentComments(doc.client, doc.comments, editorRef, me?.user.id)

  const handleRename = useCallback(
    async (newName: string) => {
      await orpc.documents.update({ projectId, documentId: docId, title: newName })
      setTitle(newName)
    },
    [projectId, docId]
  )
  useSetPageSubtitle(title ?? null, handleRename)

  useEffect(() => {
    let cancelled = false
    setLoaded(false)
    orpc.documents
      .get({ projectId, documentId: docId })
      .then((res) => {
        if (cancelled) return
        setTitle(res.document.title)
        setSourceFilePath(res.document.sourceFilePath ?? null)
        setPdfPath(res.pdfPath ?? null)
        setSpreadsheetPath(res.spreadsheetPath ?? null)
        setImagePath(res.imagePath ?? null)
        setDescription(res.description ?? null)
        setOriginalFileName(res.originalFileName ?? null)
        setConversionStatus(res.document.conversionStatus ?? null)
        setLoaded(true)
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
    return () => {
      cancelled = true
    }
  }, [projectId, docId])

  useEffect(() => {
    fetch('/health')
      .then((res) => res.json())
      .then((data: { features?: { pdfExport?: boolean } }) =>
        setPdfAvailable(data.features?.pdfExport === true)
      )
      .catch(() => setPdfAvailable(false))
  }, [])

  // Poll while conversion is in progress
  useEffect(() => {
    if (!sourceFilePath || pdfPath || spreadsheetPath || !isConverting) return
    const interval = setInterval(async () => {
      try {
        const res = await orpc.documents.get({ projectId, documentId: docId })
        if (res.pdfPath) setPdfPath(res.pdfPath)
        if (res.spreadsheetPath) setSpreadsheetPath(res.spreadsheetPath)
        // A HEIC's preview appears with the conversion; a failed picture loses it.
        setImagePath(res.imagePath ?? null)
        setDescription(res.description ?? null)
        setConversionStatus(res.document.conversionStatus ?? null)
      } catch {
        // retry on next tick
      }
    }, 3000)
    return () => clearInterval(interval)
  }, [sourceFilePath, pdfPath, spreadsheetPath, isConverting, projectId, docId])

  const handleExportPdf = useCallback(async () => {
    const editor = editorRef.current?.editor
    if (!editor || exporting) return
    setExporting('pdf')
    try {
      const body = await inlineImages(await editor.blocksToHTMLLossy(editor.document))
      const res = await fetch(`/workspaces/${projectId}/documents/export-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ html: buildPrintHtml(body, title), title }),
      })
      if (!res.ok) throw new Error(`PDF export failed: ${res.status}`)
      downloadBlob(await res.blob(), `${title ?? 'dokument'}.pdf`)
    } catch (err) {
      console.error(err)
      toast.error(te('blocks.exportFailed'))
    } finally {
      setExporting(null)
    }
  }, [projectId, title, exporting, te])

  const handlePrint = useCallback(async () => {
    const editor = editorRef.current?.editor
    if (!editor) return
    try {
      const body = await inlineImages(await editor.blocksToHTMLLossy(editor.document))
      printHtml(buildPrintHtml(body, title))
    } catch (err) {
      console.error(err)
      toast.error(te('blocks.exportFailed'))
    }
  }, [title, te])

  const handleExportDocx = useCallback(async () => {
    const blocks = doc.client?.blocks
    if (!blocks || exporting) return
    setExporting('docx')
    try {
      const { blocksToDocx } = await import('@/lib/documents/export-docx')
      downloadBlob(await blocksToDocx(blocks, title ?? 'dokument'), `${title ?? 'dokument'}.docx`)
    } catch (err) {
      console.error(err)
      toast.error(te('blocks.exportFailed'))
    } finally {
      setExporting(null)
    }
  }, [doc.client, title, exporting, te])

  const {
    openThreads,
    resolvedThreads,
    select: selectThread,
    activeId: activeThreadId,
    showResolved,
    setShowResolved,
    hidden: commentsHidden,
    setHidden: setCommentsHidden,
  } = comments
  const openCommentCount = openThreads.length
  const resolvedCount = resolvedThreads.length
  // Gutter markers (phone / narrow): open threads per block.
  const commentCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const th of openThreads) counts.set(th.blockId, (counts.get(th.blockId) ?? 0) + 1)
    return counts
  }, [openThreads])
  // Gutter marker: one thread → open it; several → the list filtered to the block.
  const openBlockThreads = useCallback(
    (blockId: string) => {
      const onBlock = openThreads.filter((th) => th.blockId === blockId)
      if (onBlock.length === 1) selectThread(onBlock[0]!.id)
      else if (onBlock.length > 1) setCommentList({ open: true, blockId })
    },
    [openThreads, selectThread]
  )
  const openCommentList = useCallback(() => setCommentList({ open: true, blockId: null }), [])
  const closeCommentList = useCallback(() => setCommentList({ open: false, blockId: null }), [])
  const jumpToThread = useCallback(
    (thread: CommentThread) => {
      editorRef.current?.scrollToBlock(thread.blockId)
      selectThread(thread.id)
      if (commentMode === 'narrow') closeCommentList()
    },
    [selectThread, commentMode, closeCommentList]
  )
  // A whole-block thread (no quoted text) highlights its block while active.
  const activeThread = openThreads.find((th) => th.id === activeThreadId)
  const highlightedBlockId = activeThread && !activeThread.quotedText ? activeThread.blockId : null

  const actions = useMemo(
    () =>
      wantsEditor ? (
        <DocumentActions
          status={doc.status}
          openCommentCount={openCommentCount}
          resolvedCount={resolvedCount}
          showResolved={showResolved}
          commentsHidden={commentsHidden}
          canUndo={history.canUndo}
          canRedo={history.canRedo}
          exporting={exporting}
          pdfAvailable={pdfAvailable}
          onUndo={() => editorRef.current?.undo()}
          onRedo={() => editorRef.current?.redo()}
          onOpenList={commentMode === 'margin' ? undefined : openCommentList}
          onToggleResolved={() => setShowResolved(!showResolved)}
          onToggleHidden={() => setCommentsHidden(!commentsHidden)}
          onPrint={handlePrint}
          onExportPdf={handleExportPdf}
          onExportDocx={handleExportDocx}
        />
      ) : null,
    [
      wantsEditor,
      doc.status,
      openCommentCount,
      resolvedCount,
      showResolved,
      setShowResolved,
      commentsHidden,
      setCommentsHidden,
      commentMode,
      openCommentList,
      history,
      exporting,
      pdfAvailable,
      handlePrint,
      handleExportPdf,
      handleExportDocx,
    ]
  )
  useSetPageActions(actions)

  // Deep link (#<blockId>) from chat artifacts / citations / mention
  // notifications, or #<threadId> from a comment-mention notification (board
  // 9p2d: scroll to the thread's block and open the thread) — once the editor
  // is up, and again when the hash changes while the document is already
  // open (a bell click on the same document).
  const locationHash = useLocation({ select: (l) => l.hash })
  const commentsRef = useRef(comments)
  commentsRef.current = comments
  useEffect(() => {
    if (!doc.ready) return
    const hash = (locationHash || window.location.hash).replace(/^#/, '')
    if (!hash) return
    const jump = (): boolean => {
      if (!COMMENT_THREAD_ID_RE.test(hash)) return !!editorRef.current?.scrollToBlock(hash)
      const state = commentsRef.current
      const thread = state.threads.find((th) => th.id === hash)
      if (!thread) return false
      if (!editorRef.current?.scrollToBlock(thread.blockId)) return false
      if (thread.resolvedAt) state.setShowResolved(true)
      state.setHidden(false)
      state.select(thread.id)
      return true
    }
    // The editor chunk is lazy and mounts after `ready`: retry for a few seconds.
    let tries = 0
    const timer = setInterval(() => {
      tries += 1
      if (jump() || tries >= 12) clearInterval(timer)
    }, 300)
    return () => clearInterval(timer)
  }, [doc.ready, locationHash])

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-destructive text-sm">{t('editor.loadError')}</p>
      </div>
    )
  }

  return (
    <div className="relative flex flex-1 flex-col overflow-hidden">
      <MeetingNotesBanner projectId={projectId} documentId={docId} />
      <Suspense
        fallback={
          <Spinner
            label={
              spreadsheetPath
                ? t('editor.loadingSpreadsheet')
                : isImageDocument
                  ? t('editor.loadingImage')
                  : isUploadedDocument
                    ? t('editor.loadingPdf')
                    : t('editor.loadingEditor')
            }
          />
        }
      >
        {isUploadedDocument && spreadsheetPath ? (
          <SpreadsheetViewer
            url={toApiUrl(`/workspaces/${projectId}/${spreadsheetPath}`)}
            originalUrl={
              sourceFilePath ? toApiUrl(`/workspaces/${projectId}/${sourceFilePath}`) : undefined
            }
            originalFilename={originalFileName ?? undefined}
          />
        ) : isUploadedDocument && pdfPath ? (
          <PdfViewer
            url={toApiUrl(`/workspaces/${projectId}/${pdfPath}`)}
            filename={
              originalFileName
                ? `${originalFileName.replace(/\.[^.]+$/, '')}.pdf`
                : title
                  ? `${title}.pdf`
                  : undefined
            }
            originalUrl={
              sourceFilePath ? toApiUrl(`/workspaces/${projectId}/${sourceFilePath}`) : undefined
            }
            originalFilename={originalFileName ?? undefined}
          />
        ) : isImageDocument && imagePath ? (
          <ImageViewer
            projectId={projectId}
            description={description}
            describing={isConverting}
            url={toApiUrl(`/workspaces/${projectId}/${imagePath}`)}
            title={title}
            originalUrl={
              sourceFilePath ? toApiUrl(`/workspaces/${projectId}/${sourceFilePath}`) : undefined
            }
            originalFilename={originalFileName ?? undefined}
          />
        ) : isUploadedDocument && isConverting ? (
          <Spinner label={t('editor.converting')} />
        ) : isImageDocument ? (
          <div className="flex flex-1 items-center justify-center px-6">
            <p className="text-muted-foreground max-w-sm text-center text-sm">
              {t('viewer.image.failed')}
            </p>
          </div>
        ) : wantsEditor && doc.client && doc.ready ? (
          <div className="flex-1 overflow-y-auto">
            {/* One measured row: the editor keeps this exact tree position in
                every comment layout mode (a conditional wrapper would remount it). */}
            <div
              ref={rowRef}
              className="relative mx-auto flex w-full items-start justify-center gap-6 px-4 py-8"
            >
              <div className="w-full min-w-0 max-w-3xl">
                <CommentHighlightStyle state={comments} />
                <BlockEditor
                  ref={setEditorRef}
                  client={doc.client}
                  projectId={projectId}
                  initialBlocks={doc.initialBlocks}
                  presence={doc.presence}
                  currentUserId={me?.user.id}
                  onSelectionChange={setSelectedBlockIds}
                  onHistoryChange={setHistory}
                  onAddComment={comments.startComposer}
                  highlightedBlockId={commentsHidden ? null : highlightedBlockId}
                  commentCounts={
                    commentMode === 'margin' || commentsHidden ? undefined : commentCounts
                  }
                  onMarkerClick={openBlockThreads}
                  onThreadHover={comments.hover}
                  onThreadClick={selectThread}
                />
              </div>
              {commentMode === 'margin' && !commentsHidden && (
                <Suspense fallback={<div className="w-72 shrink-0" />}>
                  <CommentMargin
                    state={comments}
                    client={doc.client}
                    editor={editorHandle}
                    rowEl={rowEl}
                    currentUserId={me?.user.id}
                  />
                </Suspense>
              )}
              {commentMode === 'narrow' && !commentsHidden && (
                <Suspense fallback={null}>
                  <CommentFloating
                    state={comments}
                    client={doc.client}
                    editor={editorHandle}
                    rowEl={rowEl}
                    currentUserId={me?.user.id}
                  />
                </Suspense>
              )}
            </div>
          </div>
        ) : (
          <Spinner label={te('blocks.loading')} />
        )}
      </Suspense>

      {wantsEditor && doc.client && commentMode === 'phone' && (
        <Suspense fallback={null}>
          <CommentDrawer
            state={comments}
            client={doc.client}
            currentUserId={me?.user.id}
            listOpen={commentList.open}
            listBlockId={commentList.blockId}
            onCloseList={closeCommentList}
            onJump={jumpToThread}
          />
        </Suspense>
      )}
      {wantsEditor && doc.client && commentMode === 'narrow' && (
        <Suspense fallback={null}>
          <CommentListSheet
            open={commentList.open}
            onOpenChange={(open) => !open && closeCommentList()}
            state={comments}
            client={doc.client}
            currentUserId={me?.user.id}
            onJump={jumpToThread}
            blockId={commentList.blockId}
          />
        </Suspense>
      )}

      {wantsEditor && (
        <Suspense fallback={null}>
          <HubBubble
            projectId={projectId}
            context={{
              kind: 'document',
              documentId: docId,
              documentTitle: title,
              selectedBlockIds,
              blocks: doc.client?.blocks ?? [],
              onClearSelection: () => setSelectedBlockIds([]),
              onSelectBlocks: setSelectedBlockIds,
              onJumpToBlock: (id) => editorRef.current?.scrollToBlock(id),
            }}
          />
        </Suspense>
      )}
    </div>
  )
}
